import React, { useEffect, useRef, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BaseLink } from '../../../base/Link/BaseLink';
import { BoxComponent } from '../../../base/box/Box';
import { styles } from '../styles';
import { HeaderProps } from '../types/';
import { truncateLabel } from '../shared/helpers';
import {
  BUILDER_EDITOR_ELEMENT_ID,
  HEADER_STATE,
  generateUrl,
  handleNavigationPreviewMode,
  headerInitialProps,
} from '../../Commons';
import CtaButton from '../../../components/CtaButton';
import { getDesktopStyles, getBuilderDesktopStyles } from '../shared/helpers';
import { Image } from '../../SharedComponents';
import { Box } from '@mui/material';
import { ILink } from '@zeus/shared';
import { useHeaderStyles } from '../styles/useHeaderStyles';
import styled from '@emotion/styled';
import { UniversalLink } from '../../../components/UniversalLink';
const MobileVariant = React.lazy(() => import('../shared/Components/MobileVariant'));

const StyledLink = styled(UniversalLink, {
  shouldForwardProp: (prop) => prop !== 'customStyles',
})`
  ${(props: any) => props.customStyles}
`;

const HeaderVariantOne = ({
  globalStyles = headerInitialProps.globalStyles,
  localStyles = headerInitialProps.localStyles,
  componentConfig = headerInitialProps.componentConfig,
  builderConfig = headerInitialProps.builderConfig,
  MediaMap,
  LinkComponent,
  ImageComponent,
  onTextSelect,
}: HeaderProps) => {
  const blockLinks = 'javascript:void(0)';
  const {
    button: rightButton,
    navLinks: { toggle: showNavLinks },
    background,
    allCapsToggle,
  } = componentConfig;
  const {
    logoConfig: {
      header: { primary: logoProperties, secondary: logoSecondaryProperties },
    },
    navLinks,
    isBuilderDesktopMode,
    isPublishedDesktopMode,
    builderMode,
    previewMode,
    pageUrls,
  } = builderConfig;

  const headerState = globalStyles.headerState;
  const isDesktop = isBuilderDesktopMode || isPublishedDesktopMode;

  // Determine which logo to use based on desktop/mobile and scroll state
  const getLogoImage = () => {
    // Check for mobile-specific logo
    if (!isDesktop && logoProperties?.['media-s']?.image) {
      return logoProperties['media-s'].image;
    }
    // Check for scroll state logo
    if (headerState === HEADER_STATE.SCROLL_UP && scrolled) {
      return logoSecondaryProperties?.image || logoProperties.image || '';
    }
    return logoProperties.image || '';
  };
  const [isActive, setIsActive] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visibleNavLinks, setVisibleNavLinks] = useState([...navLinks]);
  const [dropdownNavLinks, setDropdownNavLinks] = useState<ILink[]>([]);
  const [isWholeNavbarParsed, setIsWholeNavbarParsed] = useState(false);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);
  const [hoveredLinkIndex, setHoveredLinkIndex] = useState<string | null>(null);
  const [hoveredMoreButton, setHoveredMoreButton] = useState(false);

  // Refs for measuring elements
  const headerRef = useRef<HTMLElement | null>(null);
  const navLinksContainerRef = useRef<HTMLElement | null>(null);
  const navLinkRefs: any = useRef([]);
  const logoContainerRef = useRef(null);
  const buttonContainerRef = useRef<HTMLElement | null>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Styles hook
  const {
    IconStyles,
    buttonStyles,
    navLinkStyles,
    navLinkStyles_Mobile,
    dropdownContainerStyles,
    dropdownButtonStyles,
    dropdownMenuStyles,
    dropdownItemStyles,
    dropdownMobileItemStyles,
    navLinkContainerStyles,
    getHeaderStyles,
    navlinkWrapperStyles,
  } = useHeaderStyles({
    globalStyles,
    localStyles,
    background,
    rightButton,
    headerState,
    scrolled,
    builderMode,
    isDesktop,
    isActive,
    isWholeNavbarParsed,
    previewMode,
    designPreview: builderConfig?.builderMode && builderConfig?.previewMode,
  });

  const lineHeight = '30px';

  const toggleActiveClass = () => {
    setIsActive(!isActive);
  };

  const handleButtonClick = (button: any) => {
    if (previewMode) {
      handleNavigationPreviewMode(button);
    }
  };

  const handleNavLinkClick = (navLink: any) => {
    handleButtonClick(navLink);
    setIsActive(false);
    setHoveredLinkIndex(null);
  };

  const handleLinkHover = (index: string | null) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setHoveredLinkIndex(index);
  };

  const handleMoreButtonHover = (isHovered: boolean) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setHoveredMoreButton(isHovered);
  };

  const isTextMultiline = (element: HTMLElement): boolean => {
    if (!element) return false;
    const numericLineHeight = parseFloat(lineHeight);
    const numLines = Math.round(element.clientHeight / numericLineHeight);
    return numLines > 1;
  };

  const checkNavLinksWrapping = () => {
    if (!isDesktop || navLinks.length === 0) return;
    let visibleCount = visibleNavLinks.length;
    for (let i = 0; i < visibleNavLinks.length; i++) {
      const el = navLinkRefs.current[i] as HTMLElement | null;
      if (i === visibleCount - 1) {
        setIsWholeNavbarParsed(true);
      }
      if (!el) continue;

      const isWrapped = isTextMultiline(el);
      if (isWrapped) {
        visibleCount--;
        const allVisibleLinks = navLinks.slice(0, visibleCount);
        const remainingLinks = navLinks.slice(visibleCount);

        setVisibleNavLinks(allVisibleLinks);
        setDropdownNavLinks(remainingLinks);
        return;
      }
    }
  };

  useEffect(() => {
    if (isDesktop) {
      setVisibleNavLinks(navLinks);
      setDropdownNavLinks([]);
      setIsWholeNavbarParsed(false);
    }
  }, [
    navLinks,
    isDesktop,
    isLogoLoaded,
    rightButton.toggle,
    globalStyles.bodyText,
    localStyles?.navbarLinkStyles?.fontSize,
    localStyles?.navbarLinkStyles?.fontWeight,
    localStyles?.navbarLinkStyles?.letterSpacing,
    allCapsToggle,
  ]);

  useEffect(() => {
    const scrollContainer = document.querySelector(`#${BUILDER_EDITOR_ELEMENT_ID}`) as HTMLElement | null;

    const handleScroll = (scrollTop: number) => {
      setScrolled(scrollTop > 0);
    };

    const handleResize = () => {
      setIsWholeNavbarParsed(false);
    };

    // Add resize listener for both environments
    window.addEventListener('resize', handleResize);

    // Create a unified scroll handler that works in both environments
    const createScrollHandler = (getScrollTop: () => number, method: string) => {
      return () => {
        const currentScrollY = getScrollTop();
        handleScroll(currentScrollY);
      };
    };

    let scrollHandler: (() => void) | null = null;
    let cleanup: (() => void) | null = null;

    if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
      // Builder environment - use the container scroll if it's scrollable
      scrollHandler = createScrollHandler(() => scrollContainer.scrollTop, 'container');
      scrollContainer.addEventListener('scroll', scrollHandler, { passive: true });

      cleanup = () => {
        if (scrollHandler) {
          scrollContainer.removeEventListener('scroll', scrollHandler);
        }
        window.removeEventListener('resize', handleResize);
      };
    } else {
      // Deployed environment or non-scrollable container - use window scroll
      scrollHandler = createScrollHandler(() => window.scrollY || window.pageYOffset || 0, 'window');
      window.addEventListener('scroll', scrollHandler, { passive: true });

      cleanup = () => {
        if (scrollHandler) {
          window.removeEventListener('scroll', scrollHandler);
        }
        window.removeEventListener('resize', handleResize);
      };
    }

    // Return cleanup function
    return cleanup;
  }, [headerState]);

  useEffect(() => {
    if (isDesktop) {
      let interval: any = null;
      setTimeout(
        () => {
          interval = setInterval(() => {
            if (!navLinks.length || navLinkRefs.current.length > 0) {
              checkNavLinksWrapping();
              clearInterval(interval);
            }
          }, 100);
        },
        builderMode ? 0 : 300
      );
      return () => clearInterval(interval);
    }
  }, [
    visibleNavLinks,
    rightButton.toggle,
    globalStyles.bodyText,
    isLogoLoaded,
    isDesktop,
    localStyles?.navbarLinkStyles?.fontSize,
    localStyles?.navbarLinkStyles?.fontWeight,
    localStyles?.navbarLinkStyles?.letterSpacing,
    allCapsToggle,
  ]);

  const headerHeight = isDesktop && builderMode ? '110px' : '80px';

  const renderChildDropDown = (hasChildren: boolean, index: string, navLink: ILink) => {
    const isHoverd = hoveredLinkIndex === index;
    return (
      <>
        {hasChildren && isDesktop && isHoverd ? (
          <BoxComponent
            onMouseEnter={() => hasChildren && isDesktop && handleLinkHover(index)}
            onMouseLeave={() => hasChildren && isDesktop && handleLinkHover(null)}
            sx={{
              ...dropdownMenuStyles(scrolled),
              left: 0,
              right: 'auto',
            }}
          >
            {navLink.children?.map((childLink: ILink, childIndex: number) =>
              renderNavLink(
                childLink,
                `${index}-${childIndex}`,
                true,
                true,
                false,
                navLink.children.length - 1 === childIndex
              )
            )}
          </BoxComponent>
        ) : (
          <></>
        )}
      </>
    );
  };

  const renderNavLink = (
    navLink: ILink,
    index: string,
    isDropdown = false,
    isChild?: boolean,
    isSubChild?: boolean,
    isLast?: boolean
  ) => {
    const hasChildren = navLink.children && navLink.children.length > 0;
    const linkStyles = isDropdown
      ? {
          ...dropdownItemStyles(isChild, hasChildren, isSubChild, isLast),
          ...getBuilderDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles),
        }
      : getBuilderDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles);

    if (!previewMode && !builderMode) {
      return builderConfig ? (
        <Box
          onMouseEnter={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(index)}
          onMouseLeave={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(null)}
          sx={navlinkWrapperStyles(isDropdown, !!isDesktop)}
        >
          <StyledLink
            key={index}
            LinkComponent={LinkComponent}
            ref={(el: HTMLElement) => !isChild && !isDropdown && (navLinkRefs.current[index] = el)}
            customStyles={
              isDropdown
                ? {
                    ...dropdownItemStyles(isChild, hasChildren, isSubChild, isLast),
                    ...getDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles, pageUrls),
                  }
                : getDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles, pageUrls)
            }
            target={navLink.internal ? '_self' : '_blank'}
            to={navLink.noLink ? blockLinks : `${navLink.internal ? '/' : ''}${generateUrl(navLink, pageUrls)}`}
          >
            {truncateLabel(navLink.label)}
            {hasChildren && isDesktop && !isDropdown && <ExpandMoreIcon sx={{ marginLeft: '4px' }} />}
          </StyledLink>
          {/* Children dropdown */}
          {!isDropdown && (renderChildDropDown(hasChildren, index, navLink) as any)}
        </Box>
      ) : null;
    }

    return builderConfig ? (
      <Box
        id={navLink.id}
        key={navLink.id}
        onMouseEnter={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(index)}
        onMouseLeave={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(null)}
        sx={navlinkWrapperStyles(isDropdown, !!isDesktop)}
      >
        <BaseLink
          ref={(el) => !isChild && !isDropdown && (navLinkRefs.current[index] = el)}
          href={navLink.internal ? blockLinks : navLink.link}
          target={navLink.internal ? '_self' : '_blank'}
          sx={linkStyles}
          onClick={(e) => {
            if (builderMode && !previewMode && onTextSelect) {
              e.preventDefault();
              e.stopPropagation();
              onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey: 'navlink' });
            } else if (navLink.internal && !navLink.noLink) {
              handleNavLinkClick(navLink);
            }
          }}
        >
          {truncateLabel(navLink.label)}
          {hasChildren && isDesktop && !isDropdown && <ExpandMoreIcon sx={{ marginLeft: '4px' }} />}
        </BaseLink>

        {/* Children dropdown */}
        {!isDropdown && renderChildDropDown(hasChildren, index, navLink)}
      </Box>
    ) : null;
  };

  return (
    <>
      <BoxComponent component="header" sx={getHeaderStyles()}>
        <Box
          ref={headerRef}
          sx={
            isDesktop
              ? styles.HeaderContainer
              : { ...styles.MobileHeaderContainer, padding: isActive ? '0px 35px' : '0px 25px' }
          }
        >
          <Box sx={{ ...styles.ImageContainer, display: !isDesktop && isActive ? 'none' : 'flex' }}>
            {!previewMode && !builderMode ? (
              <UniversalLink
                LinkComponent={LinkComponent}
                ref={logoContainerRef}
                to={logoProperties.link || '/'}
                style={isPublishedDesktopMode ? {} : { ...styles.Logo_Mobile }}
              >
                <Image
                  imageUrl={getLogoImage()}
                  placeholderImageStyle={{
                    minWidth: '170px',
                  }}
                  altText={logoProperties.altText || logoProperties.name || 'logo image'}
                  MediaMap={MediaMap}
                  style={{ maxWidth: isDesktop ? '400px' : '100%', maxHeight: isDesktop ? '80px' : '50px' }}
                  builderMode={builderMode}
                  lazyLoad={false}
                  onLoad={() => setIsLogoLoaded(true)}
                />
              </UniversalLink>
            ) : (
              <Box ref={logoContainerRef}>
                <BaseLink href={blockLinks} sx={styles.Logo}>
                  <img
                    height={isDesktop ? '80px' : '50px'}
                    style={{ maxWidth: isDesktop ? '400px' : '100%', maxHeight: isDesktop ? '80px' : '50px' }}
                    onLoad={() => setIsLogoLoaded(true)}
                    src={getLogoImage()}
                    alt={logoProperties.altText || logoProperties.name || 'logo image'}
                  />
                </BaseLink>
              </Box>
            )}
          </Box>
          {isDesktop ? (
            <Box
              component="nav"
              aria-label="Primary navigation"
              ref={navLinksContainerRef}
              sx={navLinkContainerStyles(isActive, true)}
            >
              {/* Visible NavLinks */}
              {showNavLinks &&
                isDesktop &&
                visibleNavLinks.map((navLink: any, index: any) => renderNavLink(navLink, index))}

              {/* Dropdown for overflow links */}
              {isDesktop && dropdownNavLinks.length > 0 && (
                <BoxComponent
                  sx={dropdownContainerStyles}
                  onMouseEnter={() => handleMoreButtonHover(true)}
                  onMouseLeave={() => handleMoreButtonHover(false)}
                >
                  <BoxComponent sx={dropdownButtonStyles}>
                    More <ExpandMoreIcon sx={{ marginLeft: '4px' }} />
                  </BoxComponent>
                  <BoxComponent sx={{ ...dropdownMenuStyles(scrolled), display: hoveredMoreButton ? 'block' : 'none' }}>
                    {dropdownNavLinks.map((navLink: ILink, index: any) => (
                      <>
                        {renderNavLink(navLink, index, true, true, false, dropdownNavLinks.length - 1 === index)}
                        {navLink.children &&
                          !!navLink.children.length &&
                          navLink.children.map((navlInk: ILink, index: number) =>
                            renderNavLink(
                              navlInk,
                              `dropdown-child-${index}`,
                              true,
                              false,
                              true,
                              navLink.children.length - 1 === index
                            )
                          )}
                      </>
                    ))}
                  </BoxComponent>
                </BoxComponent>
              )}
            </Box>
          ) : (
            <React.Suspense fallback={null}>
              <MobileVariant
                navLinks={navLinks}
                builderMode={builderMode}
                previewMode={previewMode}
                rightButton={rightButton}
                buttonStyles={buttonStyles}
                pageUrls={pageUrls}
                navLinkStyles_Mobile={navLinkStyles_Mobile}
                dropdownMobileItemStyles={dropdownMobileItemStyles}
                builderConfig={builderConfig}
                navlinkWrapperStyles={navlinkWrapperStyles}
                navLinkContainerStyles={navLinkContainerStyles}
                localStyles={localStyles}
                isActive={isActive}
                setIsActive={setIsActive}
              />
            </React.Suspense>
          )}

          {rightButton.toggle && isDesktop && (
            <Box ref={buttonContainerRef} sx={styles.NavButtonContainer}>
              <CtaButton
                builderMode={builderMode}
                previewMode={previewMode}
                buttonConfig={rightButton}
                buttonStyles={buttonStyles}
                pageUrls={pageUrls}
                LinkComponent={LinkComponent}
                onButtonClick={
                  onTextSelect
                    ? (dataKey: string) => {
                        onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey });
                      }
                    : undefined
                }
                dataKey="button.text"
              />
            </Box>
          )}
          {!isDesktop && showNavLinks && Boolean(navLinks.length) && (
            <BoxComponent
              sx={{
                ...styles.MenuButtonContainer_Mobile,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: isActive ? '100%' : 'auto',
                justifyContent: 'space-between',
                marginTop: isActive ? '20px' : '0px',
              }}
            >
              {isActive && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src={
                      headerState === HEADER_STATE.SCROLL_UP && scrolled
                        ? logoSecondaryProperties.image
                        : logoProperties.image
                    }
                    alt={logoProperties.altText}
                    style={{ height: '50px', maxWidth: '100px' }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {rightButton.toggle && !builderConfig?.stickyButtonToggle && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CtaButton
                      builderMode={builderMode}
                      previewMode={previewMode}
                      buttonConfig={rightButton}
                      buttonStyles={buttonStyles}
                      pageUrls={pageUrls}
                      onButtonClick={
                        onTextSelect
                          ? (dataKey: string) => {
                              onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey });
                            }
                          : undefined
                      }
                      dataKey="button.text"
                    />
                  </Box>
                )}
                <BoxComponent
                  sx={{ cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleActiveClass();
                  }}
                >
                  {isActive ? (
                    <CloseIcon sx={{ ...IconStyles, width: '40px', height: '40px' }} />
                  ) : (
                    <MenuIcon sx={{ ...IconStyles, width: '40px', height: '40px' }} />
                  )}{' '}
                </BoxComponent>
              </Box>
            </BoxComponent>
          )}
        </Box>
      </BoxComponent>
      {headerState === HEADER_STATE.STICKY && <BoxComponent sx={{ height: headerHeight }} />}
    </>
  );
};

export default HeaderVariantOne;
